import React, { useState } from "react";
import {
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownPopover,
  DropdownItem,
  Spinner,
} from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";

import { Artefact } from "@/types/artefacts";
import { useArtefactActions } from "@/hooks/useArtefactActions";
import { useUIPreferences } from "@/stores/UIPreferencesStore";
import { getDownloadOptions } from "@/utils/downloadOptions";
import { DownloadFormat } from "@/utils/artefactFormatDetection";

interface ArtefactActionsProps {
  artefact: Artefact;
  onLineNumbersToggle?: (showLineNumbers: boolean) => void;
}

export const ArtefactActions: React.FC<ArtefactActionsProps> = ({
  artefact,
  onLineNumbersToggle,
}) => {
  const { t } = useTranslation();
  const { handleCopy, handleRefresh, handleDownloadWithFormat } = useArtefactActions();
  const { showLineNumbers, toggleLineNumbers, showAdvancedDownloadOptions } = useUIPreferences();

  const [isDownloading, setIsDownloading] = useState(false);
  const [currentFormat, setCurrentFormat] = useState<DownloadFormat | null>(null);

  const hasRefreshAction = artefact.type === "html";
  const hasLineNumbersToggle = artefact.type === "code";

  const handleToggleLineNumbers = () => {
    toggleLineNumbers();
    onLineNumbersToggle?.(!showLineNumbers);
  };

  const downloadOptions = getDownloadOptions(artefact, showAdvancedDownloadOptions);

  const handleDownloadOption = async (format: DownloadFormat) => {
    setIsDownloading(true);
    setCurrentFormat(format);

    try {
      await handleDownloadWithFormat(artefact, format);
    } finally {
      setIsDownloading(false);
      setCurrentFormat(null);
    }
  };

  return (
    // `shrink-0`: at the narrowest panel width the header row has roughly 190px to
    // divide between the type icon, the title column and this cluster. As a
    // shrinkable flex item the icon buttons collapsed towards their 16px icon and
    // lost the 32px hit target, while the clamped title column still had room to
    // give. Declared here rather than at the call site so the cluster keeps its
    // size wherever it is used.
    <div className="flex items-center gap-2 shrink-0">
      {hasRefreshAction && (
        <Tooltip>
          <TooltipTrigger>
            <Button
              isIconOnly
              className="btn-brand-primary"
              size="sm"
              onPress={() => handleRefresh(artefact.id)}
            >
              <Icon height={16} icon="solar:refresh-bold" width={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("Refresh content")}</TooltipContent>
        </Tooltip>
      )}

      {hasLineNumbersToggle && (
        <Tooltip>
          <TooltipTrigger>
            <Button
              isIconOnly
              size="sm"
              variant={showLineNumbers ? "primary" : "secondary"}
              onPress={handleToggleLineNumbers}
            >
              <Icon height={16} icon="solar:list-bold" width={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {showLineNumbers ? t("Hide line numbers") : t("Show line numbers")}
          </TooltipContent>
        </Tooltip>
      )}

      <Tooltip>
        <TooltipTrigger>
          <Button
            isIconOnly
            className="btn-brand-primary"
            size="sm"
            onPress={() => handleCopy(artefact)}
          >
            <Icon height={16} icon="solar:copy-bold" width={16} />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("Copy content")}</TooltipContent>
      </Tooltip>

      <div className="relative">
        <Tooltip>
          <TooltipTrigger>
            <div>
              <Dropdown placement="bottom-end">
                <DropdownTrigger
                  aria-label={t("Download options")}
                  className="btn-brand-primary inline-flex items-center justify-center w-8 h-8 rounded-medium"
                  isDisabled={isDownloading}
                >
                  <Icon height={16} icon="solar:download-bold" width={16} />
                </DropdownTrigger>
                <DropdownPopover>
                  <DropdownMenu
                    aria-label={t("Download format options")}
                    disabledKeys={isDownloading ? ["all"] : []}
                  >
                    {downloadOptions.map((option) => (
                      <DropdownItem
                        key={option.key}
                        onPress={() => handleDownloadOption(option.format)}
                      >
                        <div className="flex items-center gap-2">
                          {isDownloading && currentFormat === option.format ? (
                            <Spinner size="sm" />
                          ) : (
                            <Icon height={20} icon={option.icon} width={20} />
                          )}
                          <div className="flex flex-col">
                            <span>{t(option.label)}</span>
                            <span className="text-tiny text-default-400">
                              {t(option.description)}
                            </span>
                          </div>
                        </div>
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
            </div>
          </TooltipTrigger>
          <TooltipContent>{t("Download")}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
